import React, { useState, useMemo, useCallback } from 'react';
import {
  format, // Formats a date into a string according to the given format string.
  startOfWeek, // Returns the start of a week for the given date. The start of the week depends on the locale.
  endOfWeek, // Returns the end of a week for the given date. The end of the week depends on the locale.
  startOfMonth, // Returns the start of the month for the given date (i.e., the first day of the month).
  endOfMonth, // Returns the end of the month for the given date (i.e., the last day of the month).
  eachDayOfInterval, // Returns an array of dates within a specified interval (between two dates).
  isSameMonth, // Checks if two dates fall within the same month.
  isToday, // Checks if a given date is today's date.
  addDays, // Adds a specified number of days to a given date.
} from 'date-fns';

import styles from './EventCalendar.module.scss';
import Text from '../text/Text';
import { Footer } from './factory/Factory';
import useIsRtl from '../../../app/helpers/isRtl';
import getCookie from '../../../app/helpers/getCookie';
import { useTranslation } from 'react-i18next';

const EventCalendar = ({ events, month, year, MonthYearText }) => {
  const { t } = useTranslation();
  const isEnglish = getCookie('googtrans')?.includes('/ar/en');
  const isRtl = useIsRtl();
  // const isEnglish = getCookie('googtrans')?.includes('/ar/en');

  // Constants for calendar calculations
  const currentMonth = useMemo(
    () => new Date(year, month - 1, 1),
    [year, month]
  );
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(monthStart);
  const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
  const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });

  const days = useMemo(
    () => eachDayOfInterval({ start: startDate, end: endDate }),
    [startDate, endDate]
  );
  const weekdayHeaders = useMemo(() => {
    const startingDay = startOfWeek(new Date(), { weekStartsOn: 1 });
    return eachDayOfInterval({
      start: startingDay,
      end: addDays(startingDay, 6),
    }).map((day) => format(day, 'EEE'));
  }, []);

  const getEventInfoForDay = useCallback(
    (day) => {
      const formattedDay = format(day, 'yyyy-MM-dd');

      const event = events.find((e) => {
        const eventStart = format(new Date(e.startDate), 'yyyy-MM-dd');
        const eventEnd = format(new Date(e.endDate), 'yyyy-MM-dd');

        return formattedDay >= eventStart && formattedDay <= eventEnd;
      });

      if (!event) {
        return {
          color: null,
          position: null,
          isMultiDayEvent: false,
          isFirstDay: false,
          isLastDay: false,
        };
      }

      const eventStart = format(new Date(event.startDate), 'yyyy-MM-dd');
      const eventEnd = format(new Date(event.endDate), 'yyyy-MM-dd');

      return {
        color: event.colorCode,
        position:
          formattedDay === eventStart
            ? 'start'
            : formattedDay === eventEnd
              ? 'end'
              : 'middle',
        isMultiDayEvent: eventStart !== eventEnd,
        isFirstDay: formattedDay === eventStart,
        isLastDay: formattedDay === eventEnd,
      };
    },
    [events]
  );

  const renderDay = (day) => {
    const isCurrentMonth = isSameMonth(day, currentMonth);
    const eventInfo = getEventInfoForDay(day);

    let dayClass = isCurrentMonth ? styles.date : styles.otherMonthDate;
    dayClass += isToday(day) ? ` ${styles.today}` : '';
    dayClass += eventInfo.isFirstDay ? ` ${styles.firstDay}` : '';
    dayClass += eventInfo.isLastDay ? ` ${styles.lastDay}` : '';
    dayClass += eventInfo.isMultiDayEvent ? ` ${styles.MultiEvents}` : '';
    const dayStyle = eventInfo.color
      ? { backgroundColor: eventInfo.color }
      : {};

    return (
      <div
        key={format(day, 'yyyy-MM-dd')}
        className={dayClass}
        style={dayStyle}
      >
        <Text
          title={format(day, 'd')}
          variant="text-regular-16-black"
          className={styles.text}
        />
      </div>
    );
  };
  return (
    <div className={styles.calendar}>
      <div>
        <div className={styles.navigation}>
          {/* <Text title={MonthYearText} variant="text-bold-18" /> */}
          <p className={styles.monthLabel}>
            {`${t(`Filter.${format(currentMonth, 'LLLL')}`)} ${format(currentMonth, 'yyyy')}`}
          </p>
          <div
            className={`${styles.eventTitle} ${isRtl ? '' : styles.switch}`}
            style={{ flexDirection: isRtl ? 'row-reverse' : '' }}
          >
            <Text
              title={isEnglish ? 'Event' : 'common.event'}
              variant="text-bold-18"
            />
            <Text title={`( ${events.length} )`} variant="text-bold-18" />
          </div>
        </div>
        <div className={styles.weekDays} dir="ltr">
          {weekdayHeaders.map((dayName, index) => (
            <div key={index} className={styles.dayName}>
              <Text
                title={dayName}
                variant="text-bold-italic-18-black"
                className="notranslate"
              />
            </div>
          ))}
        </div>
        <div className={styles.dateGrid} dir="ltr">
          {days.map(renderDay)}
        </div>
      </div>

      <div
        className={styles.footer}
        style={{ direction: isRtl ? 'rtl' : 'ltr' }}
      >
        <Footer events={events} />
      </div>
    </div>
  );
};

export default EventCalendar;
